import { DetailProductGuildInfo } from "../type";
import type { MenuProps } from 'antd';
import { Dropdown, Space } from 'antd';
import { DownOutlined, SmileOutlined } from '@ant-design/icons';
import { useTranslation } from "react-i18next";

const DetailGuildTab = (prop: DetailProductGuildInfo) => {
    const { t } = useTranslation();

    const videoDropDown: MenuProps['items'] = prop.video?.language?.map(lang => ({
        key: lang.name,
        label: (
            <a href={lang.url || '#'} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 !no-underline">
                {lang.icon} {lang.name}
            </a>
        ),
    })) ?? [];

    const documentDropDown: MenuProps['items'] = prop.document?.language.map(lang => ({
        key: lang.name,
        label: (
            <a href={lang.url || '#'} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 !no-underline">
                {lang.icon} {lang.name}
            </a>
        ),
    }));

    return (
        <div className="p-6 space-y-4 store-container">
            <div className="border border-gray-200 rounded-[4px] p-4 guild-card hover:bg-gray-50 transition duration-200">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-red-100 rounded-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-play h-6 w-6 text-red-600"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                    </div>
                    <div className="flex-1">
                        <h3 className="font-semibold mb-2">{t('store_detail_tab_guild_video_title')}</h3>
                        <p className=" mb-3">
                            {t('store_detail_tab_guild_video_desc')}
                        </p>
                        <div className="flex items-start gap-4 text-sm mb-3">
                            <span>⏱️ {t('store_detail_tab_guild_video_duration')}: {prop.video?.duration}</span>
                        </div>
                        <div className="flex items-start gap-2 text-sm mb-3">
                            <div>
                                🌐 {t('store_detail_tab_guild_language')}:
                            </div>
                            <div className="flex items-center gap-2">
                                {prop.video?.language?.map((lang) => (
                                    <div key={lang.name} className="ml-1">
                                        {lang.icon} {lang.name}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <Dropdown menu={{ items: videoDropDown }} trigger={['click']}>
                            <button className="inline-flex items-center gap-2 !bg-red-600 !text-white px-4 py-2 !rounded-[4px] hover:!bg-red-700 transition duration-200">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-play h-4 w-4"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                                {t('store_detail_tab_guild_video_button')} | <DownOutlined />
                            </button>
                        </Dropdown>
                    </div>
                </div>
            </div>
            <div className="border border-gray-200 rounded-[4px] p-4 guild-card hover:bg-gray-50 transition duration-200">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-file-text h-6 w-6 text-blue-600"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M10 9H8"></path><path d="M16 13H8"></path><path d="M16 17H8"></path></svg>
                    </div>
                    <div className="flex-1">
                        <h3 className="font-semibold mb-2">{t('store_detail_tab_guild_doc_title')}</h3>
                        <p className="mb-3">{t('store_detail_tab_guild_doc_desc')}</p>
                        <ul className="space-y-1 mb-3">
                            <li>• {t('store_detail_tab_guild_doc_inclueItem1')}</li>
                            <li>• {t('store_detail_tab_guild_doc_inclueItem2')}</li>
                            <li>• {t('store_detail_tab_guild_doc_inclueItem3')}</li>
                            <li>• {t('store_detail_tab_guild_doc_inclueItem4')}</li>
                            <li>• {t('store_detail_tab_guild_doc_inclueItem5')}</li>
                        </ul>
                        <div className="flex items-center gap-4 text-sm mb-3">
                            <span>📄 {prop.document?.totalPages} {t('Page')}</span>
                            <span>🖼️ {t('store_detail_tab_guild_doc_inclueImg')}</span>
                        </div>
                        <div className="flex items-start gap-2 text-sm mb-3">
                            <div>
                                🌐 {t('store_detail_tab_guild_language')}:
                            </div>
                            <div className="flex items-center gap-2">
                                {prop.document?.language.map((lang) => (
                                    <div key={lang.name} className="ml-1">
                                        {lang.icon} {lang.name}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <Dropdown menu={{ items: documentDropDown }} trigger={['click']}>
                            <button className="inline-flex items-center gap-2 !bg-blue-600 !text-white px-4 py-2 !rounded-[4px] hover:!bg-blue-700 transition duration-200">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-download h-4 w-4"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" x2="12" y1="15" y2="3"></line></svg>
                                {t('store_detail_tab_guild_doc_button')} | <DownOutlined />
                            </button>
                        </Dropdown>
                    </div>
                </div>
            </div>
            <div className="guild-card-support p-4 rounded-[4px]">
                <h4 className="font-semibold text-gray-900 mb-2">🎓 {t('store_detail_tab_guild_more_title')}</h4>
                <p className="text-gray-600 mb-3">{t('store_detail_tab_guild_more_desc')}:</p>
                <ul className="text-gray-600 space-y-1">
                    <li>• {t('store_detail_tab_guild_more_inclueItem1')}</li>
                    <li>• {t('store_detail_tab_guild_more_inclueItem2')}</li>
                    <li>• {t('store_detail_tab_guild_more_inclueItem3')}</li>
                    <li>• {t('store_detail_tab_guild_more_inclueItem4')}</li>
                </ul>
            </div>
        </div>
    )
};

export default DetailGuildTab;